/**
 * RepBuddy - Password Reset Service (SCRUM-48)
 *
 * Deliberately imports nothing from lib/supabaseClient. Every other auth touchpoint in
 * this app calls Supabase straight from the component (LoginPage, RegisterForm,
 * SecuritySection), which is exactly the coupling that makes an auth provider hard to
 * replace. Password reset goes through our own backend instead: the browser never learns
 * which identity provider is behind it, so swapping that provider is a backend change.
 *
 * Both calls are unauthenticated - a user who could prove who they are would not be here.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// Shown when the backend is unreachable. Distinct from a backend-supplied error so the
// user can tell "your request was rejected" from "your request never arrived".
const NETWORK_ERROR = 'We could not reach RepBuddy just now. Check your connection and try again.';

async function postJson(path, body) {
  const r = await fetch(`${BACKEND_URL}${path}`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });

  // A 429 from the rate limiter and a 502 from a provider failure both arrive as JSON,
  // but a proxy error page might not - hence the guarded parse.
  let payload = {};
  try {
    payload = await r.json();
  } catch {
    payload = {};
  }
  return { ok: r.ok, status: r.status, payload };
}

/**
 * Ask for a reset link.
 *
 * Always resolves to a success unless the network itself failed. That is not laziness -
 * the endpoint answers identically whether or not the address has an account, and
 * surfacing anything finer here would undo that on the client.
 *
 * @param {string} email
 * @returns {Promise<{ ok: boolean, message: string }>}
 */
export async function requestPasswordReset(email) {
  try {
    const { ok, status, payload } = await postJson('/api/auth/forgot-password', { email });

    // The one case worth distinguishing: too many attempts from this browser. Saying so is
    // safe (it is a property of the requester, not of the account) and beats a generic
    // success message that leaves the user waiting for mail that is not coming.
    if (status === 429) {
      return { ok: false, message: payload.error || 'Too many attempts. Please wait a few minutes and try again.' };
    }
    if (!ok) {
      return { ok: false, message: payload.error || 'We could not process that request. Please try again.' };
    }
    return { ok: true, message: payload.message || 'If an account exists for that email, a reset link is on its way.' };
  } catch {
    return { ok: false, message: NETWORK_ERROR };
  }
}

/**
 * Spend a reset token and set the new password.
 *
 * @param {string} token     raw token from the emailed link
 * @param {string} password  new password
 * @returns {Promise<{ ok: boolean, message: string, expired?: boolean, invalid?: boolean }>}
 */
export async function submitNewPassword(token, password) {
  try {
    const { ok, status, payload } = await postJson('/api/auth/reset-password', { token, password });

    if (status === 429) {
      return { ok: false, message: payload.error || 'Too many attempts. Please wait a few minutes and try again.' };
    }
    if (!ok) {
      return {
        ok:      false,
        message: payload.error || 'We could not reset your password. Please request a new link.',
        expired: !!payload.expired,
        invalid: !!payload.invalid,
      };
    }
    return { ok: true, message: payload.message || 'Your password has been reset.' };
  } catch {
    return { ok: false, message: NETWORK_ERROR };
  }
}
